import {
    dateClassRule,
    dateFormatter,
    periodFormatter,
    dateValueGetter,
    periodValueGetter,
    dateFilterParams,
    periodFilterParams,
    dateComparator,
    periodComparator,
} from "@icarius-table/date";
import { numberComparatorMax2, numberFilterParams, numberComparatorCustom, numberFilterParamsCustom, createNumberFormatter } from "@icarius-table/number";
import { getLocalizedString } from "@icarius-localization/strings";

const mesesKeyValue = [
    { key: "1", value: "Enero" },
    { key: "2", value: "Febrero" },
    { key: "3", value: "Marzo" },
    { key: "4", value: "Abril" },
    { key: "5", value: "Mayo" },
    { key: "6", value: "Junio" },
    { key: "7", value: "Julio" },
    { key: "8", value: "Agosto" },
    { key: "9", value: "Septiembre" },
    { key: "10", value: "Octubre" },
    { key: "11", value: "Noviembre" },
    { key: "12", value: "Diciembre" },
];

const months = mesesKeyValue.map(mes => mes.value);

const getComponent = (decimals) => {
    switch (decimals) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 4:
        case 5:
            return `N${decimals}`;
        default:
            return "N2";
    }
}

const isObligatory = value => { return value === "B" };

const getColumnDefs = (entryStructure, decimals) => {
    // Primero, tengo las columnas fijas
    let columns = [];
    columns.push({
        headerName: getLocalizedString("code"),
        field: "Código de empleado",
        filter: "agTextColumnFilter",
        chartDataType: "series",
        checkboxSelection: function (params) {
            return params.columnApi.getRowGroupColumns().length === 0;
        },
        headerCheckboxSelection: function (params) {
            return params.columnApi.getRowGroupColumns().length === 0;
        },
        headerCheckboxSelectionFilteredOnly: true,
        enableRowGroup: true,
        pinned: "left",
    });

    columns.push({
        headerName: getLocalizedString("nameAndSurname"),
        field: "Apellido y nombres",
        filter: "agTextColumnFilter",
        pinned: "left",
    });

    columns.push({
        field: "Tipo de concepto String",
        headerName: getLocalizedString("conceptType"),
        filter: "agSetColumnFilter",
    });

    columns.push({
        headerName: getLocalizedString("employeesProcessConcepto"),
        field: "Concepto String",
        filter: "agSetColumnFilter",
    });

    columns.push({
        headerName: getLocalizedString("employeesProcessNombreConcepto"),
        field: "Nombre del concepto",
        filter: "agTextColumnFilter",
    });

    // Periodicidad
    switch (entryStructure.periodicity) {
        case "U": // En fecha especifica
            columns.push({
                field: "En fecha",
                headerName: getLocalizedString("onDate"),
                filter: "agTextColumnFilter",
                comparator: dateComparator,
                filterParams: dateFilterParams(),
                cellClassRules: dateClassRule,
                valueFormatter: dateFormatter,
                valueGetter: dateValueGetter,
            });
            break;
        case "T": // Temporal (Desde y hasta)
            columns.push({
                field: "Período desde",
                headerName: getLocalizedString("periodFrom"),
                filter: "agTextColumnFilter",
                comparator: periodComparator,
                filterParams: periodFilterParams(),
                valueFormatter: periodFormatter,
                valueGetter: periodValueGetter,
            });
            columns.push({
                field: "Período hasta",
                headerName: getLocalizedString("periodUntil"),
                filter: "agTextColumnFilter",
                comparator: periodComparator,
                filterParams: periodFilterParams(),
                valueFormatter: periodFormatter,
                valueGetter: periodValueGetter,
            });
            break;
        case "M": // En meses específicos
            months.forEach(mes => columns.push({
                headerName: getLocalizedString(mes),
                field: `${mes} String`,
                filter: "agSetColumnFilter",
            })
            );
            break;
        default: break;
    }

    // Horas y Minutos
    if (entryStructure.hoursMinutes) {
        columns.push({
            field: "Horas y Minutos (hh:mm)",
            headerName: getLocalizedString("hoursAndMinutesHhmmColon"),
            filter: "agTextColumnFilter",
        });
    }

    // Cantidad
    if (entryStructure.quantity) {
        columns.push({
            headerName: getLocalizedString("employeesProcessAmount"),
            field: "Cantidad",
            filter: "agTextColumnFilter",
            comparator: (p) => numberComparatorCustom(p, decimals["Cantidad"]),
            filterParams: numberFilterParamsCustom(decimals["Cantidad"]),
            chartDataType: "series",
            cellClass: "currencyValue",
            valueFormatter: (p) => createNumberFormatter(decimals["Cantidad"])(p),
        });
    }

    // Valor informado
    if (entryStructure.informedValue) {
        if (entryStructure.informedValueType === "L") {
            columns.push({
                headerName: getLocalizedString("liquidReportedValue"),
                field: "Valor informado líquido",
                filter: "agTextColumnFilter",
                comparator: (p) => numberComparatorCustom(p, decimals["Valor informado líquido"]),
                filterParams: numberFilterParamsCustom(decimals["Valor informado líquido"]),
                chartDataType: "series",
                cellClass: "currencyValue",
                valueFormatter: (p) => createNumberFormatter(decimals["Valor informado líquido"])(p),
            });
        } else {
            columns.push({
                headerName: getLocalizedString("grossReportedValue"),
                field: "Valor informado bruto",
                filter: "agTextColumnFilter",
                comparator: (p) => numberComparatorCustom(p, decimals["Valor informado bruto"]),
                filterParams: numberFilterParamsCustom(decimals["Valor informado bruto"]),
                chartDataType: "series",
                cellClass: "currencyValue",
                valueFormatter: (p) => createNumberFormatter(decimals["Valor informado bruto"])(p),
            });
        }
    }

    // Porcentaje
    if (entryStructure.percentage) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPorcentaje"),
            field: "Porcentaje",
            filter: "agTextColumnFilter",
            comparator: (p) => numberComparatorCustom(p, decimals["Porcentaje"]),
            filterParams: numberFilterParamsCustom(decimals["Porcentaje"]),
            chartDataType: "series",
            cellClass: "currencyValue",
            valueFormatter: (p) => createNumberFormatter(decimals["Porcentaje"])(p),
        });
    }

    // Base de calculo
    if (entryStructure.calculationBase) {
        columns.push({
            headerName: getLocalizedString("employeesProcessBaseCalculo"),
            field: "Base de cálculo",
            filter: "agTextColumnFilter",
            comparator: (p) => numberComparatorCustom(p, decimals["Base de cálculo"]),
            filterParams: numberFilterParamsCustom(decimals["Base de cálculo"]),
            chartDataType: "series",
            cellClass: "currencyValue",
            valueFormatter: (p) => createNumberFormatter(decimals["Base de cálculo"])(p),
        });
    }

    // Datos para reliquidación - Período de reliquidación - Meses a reliquidar
    if (entryStructure.reliquidation) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPeriodoReliquidacion"),
            field: "Período de reliquidación",
            filter: "agTextColumnFilter",
            comparator: periodComparator,
            filterParams: periodFilterParams(),
            valueFormatter: periodFormatter,
            valueGetter: periodValueGetter,
        });

        columns.push({
            headerName: getLocalizedString("employeesProcessMesesReliquidar"),
            field: "Meses a reliquidar",
            filter: "agTextColumnFilter",
            comparator: numberComparatorMax2,
            filterParams: numberFilterParams(),
            cellClass: "currencyValueNoDecimal",
            valueFormatter: createNumberFormatter(0, 0),
        });
    }

    // Honorarios - Número de comprobante
    if (entryStructure.honoraries) {
        columns.push({
            headerName: getLocalizedString("employeesProcessNumeroComprobante"),
            field: "Número de comprobante",
            filter: "agTextColumnFilter",
        });
    }

    // Pago a terceros
    if (entryStructure.thirdPartyPayment) {
        columns.push({
            headerName: getLocalizedString("thirdPartySingular"),
            field: "Tercero String",
            filter: "agTextColumnFilter",
        })
        columns.push({
            headerName: getLocalizedString("paymentMethod"),
            field: "Forma de pago String",
            filter: "agSetColumnFilter",
        })
        columns.push({
            headerName: getLocalizedString("paymentType"),
            field: "Tipo de pago String",
            filter: "agSetColumnFilter",
        })
        columns.push({
            headerName: getLocalizedString("bank"),
            field: "Banco String",
            filter: "agSetColumnFilter",
        })

        columns.push({
            headerName: getLocalizedString("bankAccount"),
            field: "Cuenta bancaria",
            filter: "agTextColumnFilter",
        });

        columns.push({
            field: "Comentarios del pago al tercero",
            headerName: getLocalizedString("paymentToThirdPartyComments"),
            filter: "agTextColumnFilter",
        });
    }

    // Periodo de imputacion
    if (entryStructure.imputationPeriod) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPeriodoImputacion"),
            field: "Periodo de imputación",
            filter: "agSetColumnFilter",
            comparator: periodComparator,
            filterParams: periodFilterParams(),
            valueFormatter: periodFormatter,
            valueGetter: periodValueGetter,
        });
    }

    // Centro de costo
    if (entryStructure.benefitCenter) {
        columns.push({
            field: "Centro de costo String",
            headerName: getLocalizedString("employeesProcessCentroBeneficio"),
            filter: "agSetColumnFilter",
        })
    }

    // Centro de costo 2
    if (entryStructure.benefitCenter2) {
        columns.push({
            field: "Centro de costo 2 String",
            headerName: getLocalizedString("costCenter2"),
            filter: "agSetColumnFilter",
        })
    }

    // Centro de costo 3
    if (entryStructure.benefitCenter3) {
        columns.push({
            field: "Centro de costo 3 String",
            headerName: getLocalizedString("costCenter3"),
            filter: "agSetColumnFilter",
        })
    }

    // Centro de costo 4
    if (entryStructure.benefitCenter4) {
        columns.push({
            field: "Centro de costo 4 String",
            headerName: getLocalizedString("costCenter4"),
            filter: "agSetColumnFilter",
        })
    }
    // Centro de costo 5
    if (entryStructure.benefitCenter5) {
        columns.push({
            field: "Centro de costo 5 String",
            headerName: getLocalizedString("costCenter5"),
            filter: "agSetColumnFilter",
        })
    }

    // Proyecto
    if (entryStructure.project) {
        columns.push({
            headerName: getLocalizedString("employeesProcessProyecto"),
            field: "Proyecto String",
            filter: "agSetColumnFilter",
        })
    }

    // Código de referencia
    if (entryStructure.codeRef) {
        columns.push({
            field: "Código de referencia String",
            headerName: getLocalizedString("employeesProcessCodigoReferencia"),
            filter: "agTextColumnFilter",
        })
    }

    // Referencia genérica 1
    if (entryStructure.genericRef1) {
        columns.push({
            field: "Referencia genérica 1",
            headerName: getLocalizedString("genericReference1"),
            filter: "agTextColumnFilter",
        })
    }

    // Referencia genérica 2
    if (entryStructure.genericRef2) {
        columns.push({
            field: "Referencia genérica 2",
            headerName: getLocalizedString("genericReference2"),
            filter: "agTextColumnFilter",
        })
    }

    // Anticipo - Fecha de descuento
    if (entryStructure.advancePayment) {
        columns.push({
            field: "Fecha de descuento",
            headerName: getLocalizedString("discountDate"),
            filter: "agTextColumnFilter",
            comparator: dateComparator,
            filterParams: dateFilterParams(),
            cellClassRules: dateClassRule,
            valueFormatter: dateFormatter,
            valueGetter: dateValueGetter,
        });
    }

    // Indicador de cálculo
    if (entryStructure.calculationIndicator) {
        columns.push({
            field: "Indicador de cálculo String",
            headerName: getLocalizedString("calculationIndicator"),
            filter: "agSetColumnFilter",
        })
    }

    columns.push({
        headerName: getLocalizedString("employeesProcessComentarios"),
        field: "Comentarios",
        filter: "agTextColumnFilter",
    });

    return columns;
};


const getFields = (entryStructure, decimals) => {
    // Primero, tengo las columnas fijas
    let columns = [];
    columns.push({
        headerName: getLocalizedString("nameAndSurname"),
        field: "Apellido y nombres",
        isObligatory: true,
        maxLength: 254,
        minLength: 0,
        component: 'A',
    });

    columns.push({
        field: "Tipo de concepto",
        headerName: getLocalizedString("conceptType"),
        isObligatory: true,
        isSelect: true,
        component: 'L',
    });

    columns.push({
        headerName: getLocalizedString("employeesProcessConcepto"),
        field: "Concepto",
        isObligatory: true,
        isSelect: true,
        component: 'L',
    });

    columns.push({
        headerName: getLocalizedString("employeesProcessNombreConcepto"),
        field: "Nombre del concepto",
        isObligatory: true,
        maxLength: 254,
        minLength: 0,
        component: 'E',
    });

    // Periodicidad
    switch (entryStructure.periodicity) {
        case "U": // En fecha especifica
            columns.push({
                field: "En fecha",
                headerName: getLocalizedString("onDate"),
                isDate: true,
                dataType: "date",
                isObligatory: true,
                component: 'DS',
            });
            break;
        case "T": // Temporal (Desde y hasta)
            columns.push({
                field: "Período desde",
                headerName: getLocalizedString("periodFrom"),
                isObligatory: true,
                component: 'P',
            });
            columns.push({
                field: "Período hasta",
                headerName: getLocalizedString("periodUntil"),
                isObligatory: true,
                component: 'P',
            });
            break;
        case "M": // En meses específicos
            months.forEach(mes => columns.push({
                headerName: getLocalizedString(mes),
                field: mes,
                isObligatory: true,
                isSelect: true,
                component: 'L',
            })
            );
            break;
        default: break;
    }

    // Horas y Minutos
    if (entryStructure.hoursMinutes) {
        columns.push({
            field: "Horas y Minutos (hh:mm)",
            headerName: getLocalizedString("hoursAndMinutesHhmmColon"),
            isObligatory: isObligatory(entryStructure.hoursMinutes),
            maxLength: 254,
            minLength: 0,
            component: 'T',
        });
    }

    // Cantidad
    if (entryStructure.quantity) {
        columns.push({
            headerName: getLocalizedString("employeesProcessAmount"),
            field: "Cantidad",
            isObligatory: isObligatory(entryStructure.quantity),
            maxLength: 254,
            minLength: 0,
            isNumeric: true,
            validationUnrequired: true,
            component: getComponent(decimals["Cantidad"]),
        });
    }

    // Valor informado
    if (entryStructure.informedValue) {
        if (entryStructure.informedValueType === "L") {
            columns.push({
                headerName: getLocalizedString("liquidReportedValue"),
                field: "Valor informado líquido",
                isObligatory: isObligatory(entryStructure.informedValue),
                maxLength: 254,
                minLength: 0,
                isNumeric: true,
                validationUnrequired: true,
                component: getComponent(decimals["Valor informado líquido"]),
            });
        } else {
            columns.push({
                headerName: getLocalizedString("grossReportedValue"),
                field: "Valor informado bruto",
                isObligatory: isObligatory(entryStructure.informedValue),
                maxLength: 254,
                minLength: 0,
                isNumeric: true,
                validationUnrequired: true,
                isQuantityEntryPerConceptSet: false,
                component: getComponent(decimals["Valor informado bruto"]),
            });
        }
    }

    // Porcentaje
    if (entryStructure.percentage) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPorcentaje"),
            field: "Porcentaje",
            isObligatory: isObligatory(entryStructure.percentage),
            maxLength: 254,
            minLength: 0,
            isNumeric: true,
            validationUnrequired: true,
            component: getComponent(decimals["Porcentaje"]),
        });
    }

    // Base de calculo
    if (entryStructure.calculationBase) {
        columns.push({
            headerName: getLocalizedString("employeesProcessBaseCalculo"),
            field: "Base de cálculo",
            isObligatory: isObligatory(entryStructure.calculationBase),
            maxLength: 254,
            minLength: 0,
            isNumeric: true,
            validationUnrequired: true,
            component: getComponent(decimals["Base de cálculo"]),
        });
    }

    // Datos para reliquidación - Período de reliquidación - Meses a reliquidar
    if (entryStructure.reliquidation) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPeriodoReliquidacion"),
            field: "Período de reliquidación",
            isObligatory: isObligatory(entryStructure.reliquidation),
            component: 'P',
        });

        columns.push({
            headerName: getLocalizedString("employeesProcessMesesReliquidar"),
            field: "Meses a reliquidar",
            isObligatory: isObligatory(entryStructure.reliquidation),
            isSelect: true,
            component: 'L',
        });
    }

    // Honorarios - Número de comprobante
    if (entryStructure.honoraries) {
        columns.push({
            headerName: getLocalizedString("employeesProcessNumeroComprobante"),
            field: "Número de comprobante",
            isObligatory: isObligatory(entryStructure.honoraries),
            maxLength: 254,
            minLength: 0,
            component: 'E',
        });
    }

    // Pago a terceros
    if (entryStructure.thirdPartyPayment) {
        columns.push({
            headerName: getLocalizedString("thirdPartySingular"),
            field: "Tercero",
            isObligatory: isObligatory(entryStructure.thirdPartyPayment),
            isSelect: true,
            component: 'L',
        })
        columns.push({
            headerName: getLocalizedString("paymentMethod"),
            field: "Forma de pago",
            isObligatory: isObligatory(entryStructure.thirdPartyPayment),
            isSelect: true,
            component: 'L',
        })
        columns.push({
            headerName: getLocalizedString("paymentType"),
            field: "Tipo de pago",
            isObligatory: isObligatory(entryStructure.thirdPartyPayment),
            isSelect: true,
            component: 'L',
        })
        columns.push({
            headerName: getLocalizedString("bank"),
            field: "Banco",
            isObligatory: false,
            isSelect: true,
            component: 'L',
        })

        columns.push({
            headerName: getLocalizedString("bankAccount"),
            field: "Cuenta bancaria",
            isObligatory: false,
            maxLength: 254,
            minLength: 0,
            component: 'E',
        });

        columns.push({
            field: "Comentarios del pago al tercero",
            headerName: getLocalizedString("paymentToThirdPartyComments"),
            isObligatory: false,
            maxLength: 254,
            minLength: 0,
            component: 'E',
        });
    }

    // Periodo de imputacion
    if (entryStructure.imputationPeriod) {
        columns.push({
            headerName: getLocalizedString("employeesProcessPeriodoImputacion"),
            field: "Periodo de imputación",
            isObligatory: isObligatory(entryStructure.imputationPeriod),
            component: 'P',
        });
    }

    // Centro de costo
    if (entryStructure.benefitCenter) {
        columns.push({
            field: "Centro de costo",
            headerName: getLocalizedString("employeesProcessCentroBeneficio"),
            isObligatory: isObligatory(entryStructure.benefitCenter),
            isSelect: true,
            component: 'L',
        })
    }

    // Centro de costo 2
    if (entryStructure.benefitCenter2) {
        columns.push({
            field: "Centro de costo 2",
            headerName: getLocalizedString("costCenter2"),
            isObligatory: isObligatory(entryStructure.benefitCenter2),
            isSelect: true,
            component: 'L',
        })
    }

    // Centro de costo 3
    if (entryStructure.benefitCenter3) {
        columns.push({
            field: "Centro de costo 3",
            headerName: getLocalizedString("costCenter3"),
            isObligatory: isObligatory(entryStructure.benefitCenter3),
            isSelect: true,
            component: 'L',
        })
    }

    // Centro de costo 4
    if (entryStructure.benefitCenter4) {
        columns.push({
            field: "Centro de costo 4",
            headerName: getLocalizedString("costCenter4"),
            isObligatory: isObligatory(entryStructure.benefitCenter4),
            isSelect: true,
            component: 'L',
        })
    }

    // Centro de costo 5
    if (entryStructure.benefitCenter5) {
        columns.push({
            field: "Centro de costo 5",
            headerName: getLocalizedString("costCenter5"),
            isObligatory: isObligatory(entryStructure.benefitCenter5),
            isSelect: true,
            component: 'L',
        })
    }

    // Proyecto
    if (entryStructure.project) {
        columns.push({
            headerName: getLocalizedString("employeesProcessProyecto"),
            field: "Proyecto",
            isObligatory: isObligatory(entryStructure.project),
            isSelect: true,
            component: 'L',
        })
    }

    // Código de referencia
    if (entryStructure.codeRef) {
        columns.push({
            field: "Código de referencia",
            headerName: getLocalizedString("employeesProcessCodigoReferencia"),
            isObligatory: Boolean(entryStructure.codeRef),
            isSelect: true,
            component: 'L',
        })
    }

    // Referencia generica 1
    if (entryStructure.genericRef1) {
        columns.push({
            field: "Referencia genérica 1",
            headerName: getLocalizedString("genericReference1"),
            isObligatory: isObligatory(entryStructure.genericRef1),
            maxLength: 20,
            minLength: 0,
            component: 'E',
        });
    }

    // Referencia generica 2
    if (entryStructure.genericRef2) {
        columns.push({
            field: "Referencia genérica 2",
            headerName: getLocalizedString("genericReference2"),
            isObligatory: isObligatory(entryStructure.genericRef2),
            maxLength: 20,
            minLength: 0,
            component: 'E',
        });
    }

    // Anticipo - Fecha de descuento
    if (entryStructure.advancePayment) {
        columns.push({
            field: "Fecha de descuento",
            headerName: getLocalizedString("discountDate"),
            isDate: true,
            isObligatory: isObligatory(entryStructure.advancePayment),
            component: 'DS',
        });
    }

    // Indicador de cálculo
    if (entryStructure.calculationIndicator) {
        columns.push({
            field: "Indicador de cálculo",
            headerName: getLocalizedString("calculationIndicator"),
            isObligatory: Boolean(entryStructure.calculationIndicator),
            isSelect: true,
            component: 'L',
        })
    }

    columns.push({
        headerName: getLocalizedString("employeesProcessComentarios"),
        field: "Comentarios",
        maxLength: 254,
        minLength: 0,
        component: 'E',
    });

    return columns;
};

const timeToDecimal = t => {
    if (t) {
        let arr = t.split(':');
        let dec = parseInt((arr[1] / 6) * 10, 10);

        return parseFloat(parseInt(arr[0], 10) + '.' + (dec < 10 ? '0' : '') + dec);
    }
}

const isValidEntryPerConceptHoursMinutesValidSyntax = e => {
    let data = e ? `${String(e.getHours()).padStart(2, "0")}:${String(e.getMinutes()).padStart(2, "0")}` : "";
    if (data !== undefined && data !== null && data !== "" && data !== "") {
        return /^(\d|0\d|1\d|2[0-3]):[0-5]\d$/.test(data)
    }
    return true;
}

const isValidEntryPerConceptHoursMinutes = (e, colaboratorData) => {
    const value = e ? `${e.getHours()}:${e.getMinutes()}` : "";
    if (value !== undefined && value !== null && value !== "" && value !== "") {
        const data = colaboratorData;
        if (data && data["HORAS DIARIAS CONTRATO"] !== null && data["HORAS DIARIAS CONTRATO"] !== "") {
            return timeToDecimal(value) <= parseFloat(data["HORAS DIARIAS CONTRATO"]);
        }
    }
    return true;
}

const isValidMax3Decimals = e => {
    if (parseFloat(String(e).replace(',', '.')) === 0) return false;
    if (e !== undefined && e !== null && e !== "" && e !== "") {
        return /^\d+(\.\d{3})*(\.\d{0,3})*$/.test(e);
    }
    return true;
}

const isValidMax3DecimalsAllowZero = e => {
    if (e !== undefined && e !== null && e !== "" && e !== "") {
        return /^\d+(\.\d{3})*(\.\d{0,3})*$/.test(e);
    }
    return true;
}

const isValidMax5Decimals = e => {
    if (parseFloat(String(e).replace(',', '.')) === 0) return false;
    if (e !== undefined && e !== null && e !== "" && e !== "") {
        return /^([1-9]\d{0,2}(\.?\d{3})*|0)(\.(\d{1,5}))?$/.test(e);
    }
    return true;
}

const isValidMax5DecimalsAllowZero = e => {
    if (e !== undefined && e !== null && e !== "" && e !== "") {
        return /^([1-9]\d{0,2}(\.?\d{3})*|0)(\.(\d{1,5}))?$/.test(e);
    }
    return true;
}

const isValidCustomDecimalsAllowNegative = (e, decimals) => {
    if (parseFloat(String(e).replace(',', '.')) === 0) return false;
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`^-?\\d+(\\.\\d{${decimals}})*(\\.\\d{0,${decimals}})*$`);
        return re.test(e);
    }
    return true;
}

const isValidCustomDecimalsAllowZeroAllowNegative = (e, decimals) => {
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`^-?\\d+(\\.\\d{${decimals}})*(\\.\\d{0,${decimals}})*$`);
        return re.test(e);
    }
    return true;
}

const isValidCustomDecimals = (e, decimals) => {
    if (parseFloat(String(e).replace(',', '.')) === 0) return false;
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`^\\d+(\\.\\d{3})*(\\.\\d{0,${decimals}})*$`);
        return re.test(e);
    }
    return true;
}

const isValidCustomDecimalsAllowZero = (e, decimals) => {
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`^\\d+(\\.\\d{3})*(\\.\\d{0,${decimals}})*$`);
        return re.test(e);
    }
    return true;
}

const isValidPeriodInverted = e => {
    if (e !== undefined && e !== null && e !== "") {
        return /20\d{2}\/(0[1-9]|10|11|12)$/.test(e);
    }
    return true;
}

const isValidPeriodFromTo = (dateStart, dateEnd) => {
    if (dateStart && dateEnd) {
        // Convierto a date y veo si fallan
        const datePeriodMin = new Date(dateStart);
        const datePeriodMax = new Date(dateEnd);

        return datePeriodMin <= datePeriodMax;
    }
    return true;
}

const isValidPercentage = (e, decimals = 2) => {
    if (parseFloat(String(e).replace(',', '.')) === 0) return false;
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`(^100(\\.0{1,3})?$)|(^([1-9](\\d)?|0)(\\.\\d{0,${decimals}})?$)`);
        return re.test(e);
    }
    return true;
}

const isValidPercentageAllowZero = (e, decimals = 2) => {
    if (e !== undefined && e !== null && e !== "") {
        const re = new RegExp(`(^100(\\.0{1,2})?$)|(^([1-9](\\d)?|0)(\\.\\d{0,${decimals}})?$)`);
        return re.test(e);
    }
    return true;
}

export {
    getColumnDefs,
    getFields,
    isValidPercentageAllowZero,
    isValidPercentage,
    isValidPeriodFromTo,
    isValidPeriodInverted,
    isValidCustomDecimalsAllowZero,
    isValidCustomDecimals,
    isValidMax3DecimalsAllowZero,
    isValidMax3Decimals,
    isValidMax5DecimalsAllowZero,
    isValidMax5Decimals,
    isValidCustomDecimalsAllowNegative,
    isValidCustomDecimalsAllowZeroAllowNegative,
    isValidEntryPerConceptHoursMinutesValidSyntax,
    isValidEntryPerConceptHoursMinutes,
}